import React from "react";
import Link from "next/link";
import classNames from "classnames";
import { MENU_ITEM } from "./types";
import {
  MenuSection,
  groupItemsIntoSections,
  splitSectionsIntoColumns,
} from "./utils/menuSections";

interface NavMenuContentProps {
  /** Already permission-filtered by the caller; this component does no gating. */
  items: MENU_ITEM[];
  activeHref?: string;
}

interface MenuItemRowProps {
  item: MENU_ITEM;
  isActive: boolean;
}

function MenuItemRow({ item, isActive }: MenuItemRowProps) {
  return (
    <Link href={item.href}>
      <div
        className={classNames(
          "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
          {
            "bg-gray-100 dark:bg-gray-700": isActive,
            "hover:bg-slate-50 dark:hover:bg-gray-700": !isActive,
          }
        )}
      >
        <div className="flex items-center">
          <div
            className={classNames(
              "w-8 h-8 rounded-full flex items-center justify-center",
              {
                "text-slate-600 dark:text-gray-400": !isActive,
                "text-slate-700 dark:text-gray-200": isActive,
              }
            )}
          >
            {item.icon}
          </div>
          <span
            className={classNames("ml-3 text-sm font-medium", {
              "text-slate-700 dark:text-gray-200": isActive,
              "text-slate-700 dark:text-gray-300": !isActive,
            })}
          >
            {item.label}
          </span>
          {item.isNew && (
            <span className="ml-2 rounded-full bg-blue-100 dark:bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
              New
            </span>
          )}
        </div>
        {item.shortcut && (
          <span
            className="ml-2 flex items-center gap-0.5"
            aria-label={`Shortcut key G then ${item.shortcut.toUpperCase()}`}
          >
            <kbd className="px-1 py-0.5 rounded border border-slate-300 dark:border-gray-600 bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 text-[10px] font-mono shadow-sm">
              G
            </kbd>
            <span className="text-[10px] text-slate-400 dark:text-gray-500 font-medium mx-0.5">
              then
            </span>
            <kbd className="px-1 py-0.5 rounded border border-slate-300 dark:border-gray-600 bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 text-[10px] font-mono shadow-sm">
              {item.shortcut.toUpperCase()}
            </kbd>
          </span>
        )}
      </div>
    </Link>
  );
}

interface MenuColumnProps {
  sections: MenuSection[];
  activeHref?: string;
}

function MenuColumn({ sections, activeHref }: MenuColumnProps) {
  return (
    <div>
      {sections.map((section) => (
        <div key={section.title} className="mb-2 last:mb-0">
          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
            {section.title}
          </div>
          {section.items.map((item) => (
            <MenuItemRow
              key={item.href}
              item={item}
              isActive={item.href === activeHref}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function NavMenuContent({ items, activeHref }: NavMenuContentProps) {
  const sections = groupItemsIntoSections(items);

  if (sections.length === 0) {
    return (
      <div className="py-2">
        {items.map((item) => (
          <MenuItemRow
            key={item.href}
            item={item}
            isActive={item.href === activeHref}
          />
        ))}
      </div>
    );
  }

  const [leftSections, rightSections] = splitSectionsIntoColumns(sections);

  // `divide-x` borders every child after the first, so an empty second column
  // would still draw a divider with nothing beside it. Too few sections to
  // fill both columns means one column, not a half-empty grid.
  if (rightSections.length === 0) {
    return (
      <div className="py-2">
        <MenuColumn sections={leftSections} activeHref={activeHref} />
      </div>
    );
  }

  return (
    <div
      className="py-2 grid grid-cols-2 gap-x-1 divide-x divide-slate-100 dark:divide-gray-700"
      data-testid="nav-menu-columns"
    >
      <MenuColumn sections={leftSections} activeHref={activeHref} />
      <MenuColumn sections={rightSections} activeHref={activeHref} />
    </div>
  );
}
